import { c as _c } from "react/compiler-runtime";
import { Formik, FormikProps, useFormikContext } from 'formik';
import Head from 'next/head';
import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import useSWR from 'swr';
import { APITeam, APITeamWithRole } from '../../../../../../typings/Team.interface';
import { getMFASetupUrl } from '../../../client/Hookdeck';
import APIMethodKeys from '../../../client/APIMethodKeys';
import { CliKeyAccessFormValues, CliKeyExpiryField, cliKeyAccessInitialValues, toCliKeyAccessInput } from '../../common/ApiKeys/CliKeyAccess';
import { ProjectAccessFields } from '../../common/ApiKeys/ProjectAccessFields';
import Alert from '../../common/base/Alert';
import Badge from '../../common/base/Badge';
import Button from '../../common/base/Button';
import { StyledCard, StyledCardSection } from '../../common/base/Card';
import Container from '../../common/base/Container';
import Icon from '../../common/base/Icon';
import Loading from '../../common/base/Loading';
import Text from '../../common/base/Text';
import { ClickableArea } from '../../common/base/Button';
import Dropdown from '../../common/Dropdown';
import RadioCardsInput from '../../common/Form/Fields/RadioCardsInput';
import { Div } from '../../common/helpers/StyledUtils';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import FullscreenLayout from '../../layouts/FullscreenLayout';
import TeamSelectionList from './Nav/TeamSelection/TeamSelectionList';
import NewOrganizationModal from './Nav/TeamSelection/NewOrganization';
import NewTeamModal from './Nav/TeamSelection/NewTeam';
import OnboardingNav from './Onboarding/OnboardingNav';
import { DashboardContext } from './DashboardContext';

// What the login can reach: only the project the CLI starts in, only that project's organization,
// only some of that organization's projects, or everything the user can.
const REACH_OPTIONS = ['project', 'organization', 'projects', 'everything'] as const;
type Reach = (typeof REACH_OPTIONS)[number];

// The project the CLI starts in, and what the login can reach: everything the user can, for good,
// unless they change it.
interface FormValues {
  team_id: string;
  reach: Reach;
  // The projects picked with 'projects'. The project the CLI starts in is always one of them.
  project_ids: string[];
  expires_in: string;
}
interface AccessProject {
  id: string;
  name: string;
  organization_id: string;
}

/** The picks, as the access the shared key helpers take. */
const toAccessValues = (values: FormValues, starting_project?: AccessProject): CliKeyAccessFormValues => ({
  expires_in: values.expires_in,
  access: values.reach === 'everything' ? 'full' : 'organization',
  organization_id: starting_project?.organization_id ?? '',
  project_access: values.reach === 'project' || values.reach === 'projects' ? 'selected' : 'all',
  // 'project' is 'projects' with nothing else picked.
  project_ids: [...new Set([...(starting_project ? [starting_project.id] : []), ...(values.reach === 'projects' ? values.project_ids : [])])]
});

// The picks are kept in the page's address as they change. The page can be drawn again while the
// dashboard loads, and setting up MFA leaves it and comes back to this address. Either way, the
// user gets their picks back rather than full access.
const DRAFT_PARAM = 'draft';
const readDraft = (search: string): FormValues | null => {
  try {
    const draft = JSON.parse(new URLSearchParams(search).get(DRAFT_PARAM) ?? 'null');
    const isString = (value: unknown) => typeof value === 'string';
    const fields = ['team_id', 'reach', 'expires_in'];
    if (!draft || !fields.every(field => isString(draft[field])) || !REACH_OPTIONS.includes(draft.reach) || !Array.isArray(draft.project_ids) || !draft.project_ids.every(isString)) {
      return null;
    }
    return {
      team_id: draft.team_id,
      reach: draft.reach,
      project_ids: draft.project_ids,
      expires_in: draft.expires_in
    };
  } catch {
    return null;
  }
};

// Writes the picks into the address each time they change. Until they do, the address is left
// alone, so a plain Connect CLI doesn't touch it.
const KeepPicksInAddress: React.FC = () => {
  const $ = _c(6);
  const {
    values
  } = useFormikContext();
  const history = useHistory();
  let t0;
  if ($[0] !== values) {
    t0 = JSON.stringify(values);
    $[0] = values;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const draft_json = t0;
  const initial_draft_json = useRef(draft_json);
  let t1;
  let t2;
  if ($[2] !== draft_json || $[3] !== history) {
    t1 = () => {
      const search = new URLSearchParams(window.location.search);
      const saved_draft_json = search.get(DRAFT_PARAM);
      if (saved_draft_json === draft_json || saved_draft_json === null && draft_json === initial_draft_json.current) {
        return;
      }
      search.set(DRAFT_PARAM, draft_json);
      history.replace({
        search: search.toString()
      });
    };
    t2 = [draft_json, history];
    $[2] = draft_json;
    $[3] = history;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  return null;
};
interface ReachFieldsProps {
  organizations: {
    id: string;
    name: string;
  }[];
  projects: AccessProject[];
}

// What the CLI can reach, then how long for. The organization is the starting project's.
const ReachFields: React.FC<ReachFieldsProps> = t0 => {
  const $ = _c(37);
  const {
    organizations,
    projects
  } = t0;
  const {
    values
  } = useFormikContext();
  let t1;
  if ($[0] !== projects || $[1] !== values.team_id) {
    let t2;
    if ($[3] !== values.team_id) {
      t2 = project => project.id === values.team_id;
      $[3] = values.team_id;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = projects.find(t2);
    $[0] = projects;
    $[1] = values.team_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const starting_project = t1;
  let t2;
  if ($[5] !== organizations || $[6] !== starting_project?.organization_id) {
    t2 = organizations.find(organization => organization.id === starting_project?.organization_id)?.name;
    $[5] = organizations;
    $[6] = starting_project?.organization_id;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const organization_name = t2;
  let t3;
  if ($[8] !== projects || $[9] !== starting_project?.organization_id) {
    let t4;
    if ($[11] !== starting_project?.organization_id) {
      t4 = project_0 => project_0.organization_id === starting_project?.organization_id;
      $[11] = starting_project?.organization_id;
      $[12] = t4;
    } else {
      t4 = $[12];
    }
    t3 = projects.filter(t4);
    $[8] = projects;
    $[9] = starting_project?.organization_id;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const organization_projects = t3;
  const t4 = starting_project ? `Only "${starting_project.name}"` : "Only this project";
  let t5;
  if ($[13] !== t4) {
    t5 = {
      value: "project",
      label: t4,
      description: "Just this project."
    };
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const t6 = organization_name ? `Only "${organization_name}" organization` : "Only this organization";
  let t7;
  if ($[15] !== t6) {
    t7 = {
      value: "organization",
      label: t6,
      description: "This organization and all its projects."
    };
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const t8 = organization_name ? `Pick which projects of "${organization_name}".` : "Pick which projects of this organization.";
  let t9;
  if ($[17] !== t8) {
    t9 = {
      value: "projects",
      label: "Only some projects",
      description: t8
    };
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      value: "everything",
      label: "Everything",
      description: "All your organizations and projects."
    };
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t5 || $[21] !== t7 || $[22] !== t9) {
    t11 = [t5, t7, t9, t10];
    $[20] = t5;
    $[21] = t7;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const options = t11;
  let t12;
  let t13;
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      direction: "column",
      gap: 0
    };
    t13 = {
      b: 4
    };
    t14 = <Text semi={true} size="xs" muted={true} as="label">Access</Text>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t12 = $[24];
    t13 = $[25];
    t14 = $[26];
  }
  let t15;
  if ($[27] !== options) {
    t15 = <Div flex={t12} m={t13}>{t14}<RadioCardsInput name="reach" options={options} vertical={true} /></Div>;
    $[27] = options;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== organization_projects || $[30] !== values.reach || $[31] !== values.team_id) {
    t16 = values.reach === "projects" && <Div m={{
      b: 4
    }}><ProjectAccessFields projects={organization_projects} current_project_id={values.team_id} locked_project_id={values.team_id} list_only={true} /></Div>;
    $[29] = organization_projects;
    $[30] = values.reach;
    $[31] = values.team_id;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <CliKeyExpiryField />;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== t15 || $[35] !== t16) {
    t18 = <>{t15}{t16}{t17}</>;
    $[34] = t15;
    $[35] = t16;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  return t18;
};
const CliAuthView: React.FC = () => {
  const $ = _c(65);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const {
    addToast
  } = useToasts();
  const history = useHistory();
  const location = useLocation();
  const [key_claimed, setKeyClaimed] = useState(false);
  const [error, setError] = useState(null);
  let t0;
  if ($[0] !== location.search) {
    t0 = () => readDraft(location.search);
    $[0] = location.search;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const [draft] = useState(t0);
  const form_ref = useRef(null);
  const {
    team
  } = useContext(DashboardContext);
  const [selected_team, setSelectedTeam] = useState(team || null);
  const match = useRouteMatch();
  const [modal, setModal] = useState(null);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.teams.listAll();
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== HookdeckAPI.teams) {
    t2 = () => HookdeckAPI.teams.listAll();
    $[3] = HookdeckAPI.teams;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data: teams,
    mutate: mutateTeams,
    isValidating: loading_teams
  } = useSWR(t1, t2);
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = APIMethodKeys.organizations.listAll();
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== HookdeckAPI.organizations) {
    t4 = () => HookdeckAPI.organizations.listAll();
    $[6] = HookdeckAPI.organizations;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    data: organizations,
    mutate: mutateOrganizations,
    isValidating: loading_organizations
  } = useSWR(t3, t4);
  const loading = teams === undefined && loading_teams || organizations === undefined && loading_organizations;
  const needs_onboarding_before_claim = !key_claimed && !loading && (teams?.length ?? 0) === 0;
  let t5;
  if ($[8] !== draft || $[9] !== teams) {
    t5 = draft ? teams?.find(t => t.id === draft.team_id) : undefined;
    $[8] = draft;
    $[9] = teams;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const draft_team = t5;
  const is_draft_team_shown = useRef(false);
  let t6;
  let t7;
  if ($[11] !== draft_team) {
    t6 = () => {
      if (draft_team && !is_draft_team_shown.current) {
        is_draft_team_shown.current = true;
        setSelectedTeam(draft_team);
      }
    };
    t7 = [draft_team];
    $[11] = draft_team;
    $[12] = t6;
    $[13] = t7;
  } else {
    t6 = $[12];
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[14] !== history || $[15] !== location.pathname || $[16] !== location.search || $[17] !== needs_onboarding_before_claim) {
    t8 = () => {
      if (!needs_onboarding_before_claim) {
        return;
      }
      const return_path = `${location.pathname}${location.search}`;
      history.replace(`/onboarding?redirect=${encodeURIComponent(return_path)}`);
    };
    t9 = [needs_onboarding_before_claim, history, location.pathname, location.search];
    $[14] = history;
    $[15] = location.pathname;
    $[16] = location.search;
    $[17] = needs_onboarding_before_claim;
    $[18] = t8;
    $[19] = t9;
  } else {
    t8 = $[18];
    t9 = $[19];
  }
  useEffect(t8, t9);
  let t10;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = team_for_display => {
      if (team_for_display) {
        setSelectedTeam(team_for_display);
      }
      setKeyClaimed(true);
    };
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const completeClaim = t10;
  let t11;
  if ($[21] !== addToast || $[22] !== mutateOrganizations || $[23] !== mutateTeams) {
    t11 = async team_0 => {
      await Promise.all([mutateOrganizations(), mutateTeams()]);
      setSelectedTeam(team_0);
      form_ref.current?.setFieldValue("team_id", team_0.id);
      setModal(null);
      addToast("success", `Project ${team_0.name} created and selected for CLI`);
    };
    $[21] = addToast;
    $[22] = mutateOrganizations;
    $[23] = mutateTeams;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const handleTeamCreated = t11;
  let t12;
  if ($[25] !== HookdeckAPI.session || $[26] !== match.params.key || $[27] !== selected_team || $[28] !== teams) {
    t12 = values => {
      const starting_project = teams?.find(t_0 => t_0.id === values.team_id);
      const access = toCliKeyAccessInput(toAccessValues(values, starting_project), teams ?? []);
      return HookdeckAPI.session.claimCliAuthToken(match.params.key, values.team_id, access).then(() => {
        const claimed_team = teams?.find(t_1 => t_1.id === values.team_id) || selected_team;
        completeClaim(claimed_team || null);
      }).catch(err => {
        const mfa_setup_url = getMFASetupUrl(err);
        if (mfa_setup_url) {
          window.location.assign(mfa_setup_url);
        } else {
          if (err?.response?.code === "CLI_KEY_ACCESS_INVALID") {
            setError(err.response.message);
          } else {
            if (err?.status === 404) {
              setError("This authentication link is invalid or has expired. Please run \"hookdeck login\" again to generate a new authentication link.");
            } else {
              if (err?.status === 401) {
                setError("Authentication failed. Please ensure you are logged in and try again.");
              } else {
                setError("Unable to authenticate your CLI. Please try running \"hookdeck login\" again or contact support if the issue persists.");
              }
            }
          }
        }
      });
    };
    $[25] = HookdeckAPI.session;
    $[26] = match.params.key;
    $[27] = selected_team;
    $[28] = teams;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  const handleSubmit = t12;
  let t13;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (team_1, toggle) => {
      setSelectedTeam(team_1);
      toggle(false);
    };
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  const handleTeamSelect = t13;
  let t14;
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = [];
    t15 = cliKeyAccessInitialValues();
    $[31] = t14;
    $[32] = t15;
  } else {
    t14 = $[31];
    t15 = $[32];
  }
  const t16 = (draft_team ?? selected_team)?.id || "";
  let t17;
  if ($[33] !== draft || $[34] !== t16) {
    t17 = {
      reach: "everything",
      project_ids: t14,
      expires_in: t15.expires_in,
      ...draft,
      team_id: t16
    };
    $[33] = draft;
    $[34] = t16;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const initial_values = t17;
  const validate = _temp;
  const show_loading = loading || needs_onboarding_before_claim;
  let t18;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Head><meta name="referrer" content="no-referrer" /></Head>;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== handleTeamCreated || $[38] !== modal?.type) {
    t19 = modal?.type === "organization" && <NewOrganizationModal onClose={() => setModal(null)} onSuccess={handleTeamCreated} />;
    $[37] = handleTeamCreated;
    $[38] = modal?.type;
    $[39] = t19;
  } else {
    t19 = $[39];
  }
  let t20;
  if ($[40] !== handleTeamCreated || $[41] !== modal) {
    t20 = modal?.type === "project" && modal.organization_id && <NewTeamModal organization_id={modal.organization_id} onClose={() => setModal(null)} onSuccess={handleTeamCreated} />;
    $[40] = handleTeamCreated;
    $[41] = modal;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <OnboardingNav />;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = {
      justify: "center",
      align: "center",
      direction: "column"
    };
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== key_claimed) {
    t23 = key_claimed ? <Div m={{
      b: 6
    }}><Div flex={{
        align: "center",
        justify: "center"
      }}><Icon icon="success_circle" large={true} left={true} success={true} /><Text bold={true} size="2xl">CLI authenticated</Text></Div><Text muted={true} m={{
        t: 1
      }}>Hookdeck CLI has successfully connected to your project.</Text></Div> : <Div m={{
      b: 6
    }}><Text bold={true} size="2xl">Connect Hookdeck CLI</Text><Text muted={true} m={{
        t: 1
      }}>Pick the project the CLI starts in, and what it can reach.</Text></Div>;
    $[45] = key_claimed;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== error) {
    t24 = error && <Div m={{
      b: 2
    }} w={100}><Alert inline={true} danger={true}>{error}</Alert></Div>;
    $[47] = error;
    $[48] = t24;
  } else {
    t24 = $[48];
  }
  let t25;
  if ($[49] !== handleSubmit || $[50] !== initial_values || $[51] !== key_claimed || $[52] !== organizations || $[53] !== selected_team || $[54] !== show_loading || $[55] !== teams) {
    t25 = <StyledCard raised={true} overflow_hidden={true} p={4}>{key_claimed ? <Div flex={{
        justify: "center",
        align: "center",
        direction: "column",
        gap: 4
      }}><Button p={{
          r: 1.5
        }} outline={true} large={true} block={true} style={{
          justifyContent: "space-between"
        }}><Text ellipsis={true}><Icon icon="folder" left={true} />{selected_team?.name}</Text><Badge success={true} subtle={true}><Icon icon="done_all" left={true} success={true} />CLI Connected</Badge></Button><Button to="/" block={true} primary={true}>Go to project</Button></Div> : show_loading ? <Loading /> : <Formik innerRef={form_ref} initialValues={initial_values} validate={validate} onSubmit={handleSubmit}>{props => <form onSubmit={props.handleSubmit}><KeepPicksInAddress /><Div m={{
            b: 4
          }}><Text semi={true} size="xs" muted={true} as="label">Default project</Text><Dropdown block={true} outline={true} placement="bottom-start" renderToggle={(opened, toggle_0) => <Button outline={true} large={true} block={true} onClick={() => toggle_0(!opened)} style={{
              justifyContent: "space-between"
            }}><Text ellipsis={true} muted={!selected_team} danger={props.submitCount > 0 && !!props.errors.team_id}>{selected_team && <Icon icon="folder" left={true} />}{selected_team?.name || "Select a project..."}</Text><Icon icon="expand_all" muted={!selected_team} /></Button>}>{toggle_1 => <TeamSelectionList organizations={organizations} teams={teams} selected_team_id={selected_team?.id} onTeamSelect={team_2 => {
                handleTeamSelect(team_2, toggle_1);
                props.setFieldValue("team_id", team_2.id);
              }} footer={organizations && organizations.length > 0 ? t26 => {
                const {
                  focused_organization_id
                } = t26;
                return {
                  organizations: <StyledCardSection m={{
                    t: "auto"
                  }} p={1}><ClickableArea onClick={() => setModal({
                      type: "organization"
                    })} p={{
                      y: 1.5,
                      x: 2
                    }} rounded={true} flex={{
                      align: "center"
                    }}><Icon icon="add_circle" muted={true} left={true} pointer={true} /><Text>Create organization</Text></ClickableArea></StyledCardSection>,
                  teams: focused_organization_id ? <StyledCardSection m={{
                    t: "auto"
                  }} p={1}><ClickableArea onClick={() => setModal({
                      type: "project",
                      organization_id: focused_organization_id
                    })} p={{
                      y: 1.5,
                      x: 2
                    }} rounded={true} flex={{
                      align: "center"
                    }}><Icon icon="add_circle" muted={true} left={true} pointer={true} /><Text>New project</Text></ClickableArea></StyledCardSection> : null
                };
              } : undefined} />}</Dropdown>{props.submitCount > 0 && props.errors.team_id && <Text m={{
              t: 1,
              b: 0
            }} size="s" as="p" danger={true}><Icon icon="info" left={1} />{props.errors.team_id}</Text>}</Div><Div m={{
            b: 6
          }}><ReachFields organizations={organizations ?? []} projects={teams ?? []} /></Div><Button block={true} primary={true} submit={true} disabled={props.isSubmitting}>Connect CLI</Button></form>}</Formik>}</StyledCard>;
    $[49] = handleSubmit;
    $[50] = initial_values;
    $[51] = key_claimed;
    $[52] = organizations;
    $[53] = selected_team;
    $[54] = show_loading;
    $[55] = teams;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== t23 || $[58] !== t24 || $[59] !== t25) {
    t26 = <FullscreenLayout center={true}>{t21}<Container small={true} stretch={true} flex={t22}>{t23}{t24}{t25}</Container></FullscreenLayout>;
    $[57] = t23;
    $[58] = t24;
    $[59] = t25;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] !== t19 || $[62] !== t20 || $[63] !== t26) {
    t27 = <>{t18}{t19}{t20}{t26}</>;
    $[61] = t19;
    $[62] = t20;
    $[63] = t26;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  return t27;
};
export default CliAuthView;
function _temp(values_0) {
  const errors = {};
  if (!values_0.team_id) {
    errors.team_id = "Please select a project";
  }
  return errors;
}